import {Caption} from '@jetbrains/ring-ui/components/button-group/button-group'
import type {ToggleTagParams} from '@jetbrains/ring-ui/components/tags-input/tags-input'
import TagsInput from '@jetbrains/ring-ui/components/tags-input/tags-input'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import * as React from 'react'

import {getBuildTypeTagsArg} from '../../../../../../../rest/buildTypeTags'
import {restApi} from '../../../../../../../services/rest'
import type {RingTag} from '../../../../../../../types'
import {emptyArray} from '../../../../../../../utils/empty'
import type {CleanupHolderNode} from '../../Rules.types'
import {LoadingContext, DispatchContext} from '../CleanupForm.contexts'
import styles from '../CleanupForm.css'

export const tagsConverter = {
  toRule: (tags: ReadonlyArray<RingTag>): string => tags.map(tag => tag.key).join('\n'),
  toForm: (tagsList: string | null | undefined): ReadonlyArray<RingTag> =>
    (typeof tagsList === 'string' &&
      tagsList
        .split('\n')
        .filter(Boolean)
        .map(item => ({
          key: item,
          label: item,
          angled: true,
        }))) ||
    [],
}
type OwnProps = {
  value: ReadonlyArray<RingTag>
  holderNode: CleanupHolderNode
}

function TagSection({value, holderNode}: OwnProps) {
  const {availableTags} = restApi.endpoints.getBuildTypeBuildTags.useQuery(
    holderNode.nodeType === 'bt' && holderNode.id != null
      ? getBuildTypeTagsArg(holderNode.id)
      : skipToken,
    {selectFromResult: ({data}) => ({availableTags: data ?? emptyArray})},
  )

  const disabled = React.useContext(LoadingContext)
  const setValue = React.useContext(DispatchContext)
  const onChange = React.useCallback(
    (values: RingTag[]) =>
      setValue?.({
        name: 'tags',
        value: values,
      }),
    [setValue],
  )
  const onAddTag = React.useCallback(
    ({tag}: ToggleTagParams) => onChange([...value, tag]),
    [onChange, value],
  )
  const onRemoveTag = React.useCallback(
    ({tag}: ToggleTagParams) => onChange(value.filter(item => item.key !== tag.key)),
    [onChange, value],
  )
  const dataSource = React.useCallback(
    () =>
      availableTags.map(tagName => ({
        key: tagName,
        label: tagName,
      })),
    [availableTags],
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
      event.preventDefault()
    }
  }

  return (
    <div className={styles.row}>
      <Caption className={styles.caption}>{'Tags'}</Caption>
      <div className={styles.content}>
        <div className={classNames(styles.inputs)} onKeyDown={handleKeyDown} role="presentation">
          <TagsInput
            disabled={disabled}
            placeholder={'Select tags to add'}
            tags={value}
            dataSource={dataSource}
            allowAddNewTags
            filter
            onAddTag={onAddTag}
            onRemoveTag={onRemoveTag}
          />
        </div>
        <div className={styles.hint}>{'Matches builds with any specified tag'}</div>
      </div>
    </div>
  )
}

export default React.memo<OwnProps>(TagSection)
